import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable, tap } from 'rxjs';
import { environment } from 'src/app/environments/environment';

// Define user model
export interface User {
  email: string;
  name: string;
  provider: 'azure' | 'okta';
  roles?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ClientssoService {
  apiUrl = environment.baseApiUrl;
  private tokenKey = 'access_token_explorer';
  private userKey = 'user_info_explorer';

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(
    this.hasValidToken(),
  );
  private userSubject = new BehaviorSubject<User | null>(this.getStoredUser());

  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public currentUser$ = this.userSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  // ============= LOGIN METHODS =============

  // Login with Azure AD (via backend)
  loginWithAzureAD(): void {
    // Store current URL to return after login
    const returnUrl = window.location.pathname;
    // Redirect to backend, which will initiate Azure AD OIDC flow
    window.location.href = `${this.apiUrl}api/auth/okta/loginKantar`;
  }

  // Login with Okta (SAML via backend)
  loginWithOkta(): void {
    const returnUrl = window.location.pathname;
    window.location.href = `${this.apiUrl}api/auth/okta/login`;
  }

  // ============= CALLBACK HANDLING =============

  // Call this in your callback component/route
  async handleAuthCallback(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');
    const returnUrl = urlParams.get('returnUrl') || '/landing';

    if (error) {
      console.error('Authentication error:', error);
      //this.clearAuth();
      // this.router.navigate(['/landing'], {
      //   queryParams: { error: 'Authentication failed. Please try again.' },
      // });
      //return;
    }

    if (token) {
      // Store the token
      localStorage.setItem(this.tokenKey, token);

      // Decode token to get user info (or call /me endpoint)
      this.fetchUserInfo().subscribe({
        next: (user) => {
          this.userSubject.next(user);
          this.isAuthenticatedSubject.next(true);

          window.location.href = returnUrl;
          // // Clean URL and redirect
          // this.router
          //   .navigate([returnUrl], { replaceUrl: true })
          //   .then((success) => {
          //     console.log('Navigation success:', success);
          //     if (!success) {
          //       console.error('Navigation failed!');
          //       // Try alternative navigation
          //       this.router.navigate(['/landing']);
          //     }
          //   });
        },
        error: (err) => {
          console.error('Failed to fetch user info:', err);
          this.router.navigate(['/landing']);
        },
      });
    } else {
      // No token - user probably not authenticated
      this.router.navigate(['/landing']);
    }
  }

  // Fetch current user info using the token
  fetchUserInfo(): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}api/auth/okta/me`).pipe(
      tap((user) => {
        localStorage.setItem(this.userKey, JSON.stringify(user));
      }),
    );
  }

  // Fetch current user info using the token
  fetchAzureUserInfo(): Observable<User> {
    return this.http
      .get<User>(`${this.apiUrl}api/auth/okta/getAzureToken`)
      .pipe(
        tap((user) => {
          localStorage.setItem(this.userKey, JSON.stringify(user));
        }),
      );
  }

  // ============= TOKEN MANAGEMENT =============

  getToken(): string {
    let token = localStorage.getItem(this.tokenKey);
    if (token && this.isTokenExpired(token)) {
      this.clearAuth();
      token = null;
    }
    return token || '';
  }

  private hasValidToken(): boolean {
    const token = this.getToken();
    if (!token) return false;

    // Check if token is expired
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expired = payload.exp * 1000 < Date.now();
      if (expired) {
        this.clearAuth();
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  private getStoredUser(): User | null {
    const userStr = localStorage.getItem(this.userKey);
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  // ============= LOGOUT =============

  logout(): void {
    const token = this.getToken();

    // Call backend logout (optional - to invalidate server-side session)
    this.http.post(`${this.apiUrl}api/auth/okta/logout`, {}).subscribe({
      next: () => {
        this.clearAuth();
        this.router.navigate(['/login']);
      },
      error: () => {
        // Still clear local auth even if backend fails
        this.clearAuth();
        this.router.navigate(['/login']);
      },
    });
  }

  private clearAuth(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    if (this.isAuthenticatedSubject) this.isAuthenticatedSubject.next(false);
    if (this.userSubject) this.userSubject.next(null);
  }

  // ============= HTTP INTERCEPTOR HELPERS =============

  // Get auth header for API requests
  getAuthHeader(): { Authorization: string } | {} {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  isAuthenticated(): boolean {
    return this.checkAuthenticated();
  }

  // ✅ Check if user is authenticated by validating token
  private checkAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    // Check if token is expired
    if (this.isTokenExpired(token)) {
      this.clearAuth();
      return false;
    }

    return true;
  }

  // ✅ Check if token is expired
  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      return expiry < Date.now();
    } catch {
      return true;
    }
  }

  // ✅ Get user from stored token or API
  getCurrentUser(): User | null {
    return this.userSubject.value;
  }

  clearStorages() {
    sessionStorage.clear();
    localStorage.clear();
  }
}
